// src/components/PurposeSelectionStep.jsx
import React, { useState } from "react";
import {
  Lock,
  Sparkles,
  Share2,
  Info,
  ClipboardList,
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Layers,
} from "lucide-react";
import { retentionPolicy } from "./data/mockData";

const ICONS = { Lock, Sparkles, Share2 };

const TONES = {
  red: { bg: "var(--red-50)", fg: "var(--red-600)" },
  green: { bg: "var(--green-50)", fg: "var(--green-600)" },
  purple: { bg: "var(--purple-50)", fg: "var(--purple-600)" },
};

function PurposeColumn({ groupKey, group, onToggle }) {
  const Icon = ICONS[group.icon];
  const tone = TONES[group.accent];

  return (
    <div
      style={{
        border: "1px solid var(--gray-200)",
        borderRadius: "var(--radius-md)",
        padding: 16,
        flex: 1,
        minWidth: 200,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: tone.bg,
            color: tone.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={14} />
        </span>
        <h4 style={{ fontSize: 14, fontWeight: 800, color: "var(--gray-900)", margin: 0 }}>
          {group.title}
        </h4>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
        {group.items.map((item) => (
          <div key={item.id} style={{ display: "flex", gap: 9 }}>
            <input
              type="checkbox"
              checked={item.checked}
              disabled={group.locked}
              onChange={() => onToggle(groupKey, item.id)}
              style={{
                width: 16,
                height: 16,
                marginTop: 1,
                accentColor: "var(--blue-700)",
                cursor: group.locked ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "var(--gray-900)",
                  }}
                >
                  {item.label}
                </span>
                <Info
                  size={13}
                  style={{ color: "var(--gray-400)", flexShrink: 0, cursor: "help" }}
                  aria-label={item.description}
                />
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "var(--gray-500)",
                  margin: "2px 0 0",
                  lineHeight: 1.4,
                }}
              >
                {item.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PurposeSelectionStep({
  purposes,
  onTogglePurpose,
  onBack,
  onContinue,
}) {
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  return (
    <section
      style={{
        background: "#fff",
        borderRadius: "var(--radius-lg)",
        border: "1px solid var(--gray-200)",
        boxShadow: "var(--shadow-sm)",
        padding: "20px 28px 28px",
        width: "100%",
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "var(--blue-700)",
          background: "var(--blue-50)",
          padding: "3px 9px",
          borderRadius: 5,
          letterSpacing: 0.3,
        }}
      >
        STEP 2 OF 4
      </span>
      <h2 style={{ fontSize: 21, fontWeight: 700, color: "var(--gray-900)", margin: "10px 0 4px" }}>
        Purpose Selection
      </h2>
      <p style={{ fontSize: 13.5, color: "var(--gray-500)", margin: "0 0 16px" }}>
        Select the purposes for which you provide consent
      </p>

      {/* ── Section Header Tabs ── */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 20,
          borderBottom: "1px solid var(--gray-200)",
          paddingBottom: 10,
          overflowX: "auto",
          alignItems: "center",
        }}
      >
        {[
          { id: "all", label: "All Sections", icon: Layers, color: "#2563eb" },
          { id: "mandatory", label: "Mandatory Purposes", icon: Lock, color: "#dc2626" },
          { id: "optional", label: "Optional Purposes", icon: Sparkles, color: "#16a34a" },
          { id: "thirdParty", label: "Third-Party Sharing", icon: Share2, color: "#7c3aed" },
          { id: "retention", label: "Retention & Control", icon: ClipboardList, color: "#ea580c" },
        ].map((tab) => {
          const IconComponent = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 16px",
                borderRadius: "8px 8px 0 0",
                fontSize: 13,
                fontWeight: isActive ? 800 : 700,
                color: isActive ? "#1d4ed8" : "#1e293b",
                background: isActive ? "#eff6ff" : "transparent",
                border: "none",
                borderTop: isActive ? "3px solid #2563eb" : "3px solid transparent",
                cursor: "pointer",
                transition: "all 0.15s ease",
                whiteSpace: "nowrap",
                outline: "none",
                boxShadow: "none",
              }}
            >
              {IconComponent && <IconComponent size={15} style={{ color: tab.color, flexShrink: 0 }} />}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "stretch" }}>
        {(activeTab === "all" || activeTab === "mandatory") && (
          <PurposeColumn
            groupKey="mandatory"
            group={purposes.mandatory}
            onToggle={onTogglePurpose}
          />
        )}
        {(activeTab === "all" || activeTab === "optional") && (
          <PurposeColumn
            groupKey="optional"
            group={purposes.optional}
            onToggle={onTogglePurpose}
          />
        )}
        {(activeTab === "all" || activeTab === "thirdParty") && (
          <PurposeColumn
            groupKey="thirdParty"
            group={purposes.thirdParty}
            onToggle={onTogglePurpose}
          />
        )}

        {/* Retention & control card */}
        {(activeTab === "all" || activeTab === "retention") && (
          <div
            style={{
              width: 230,
              flexShrink: 0,
              background: "var(--orange-50)",
              border: "1px solid var(--orange-100)",
              borderRadius: "var(--radius-md)",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 7,
                  background: "#fff",
                  color: "var(--orange-600)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ClipboardList size={14} />
              </span>
              <h4 style={{ fontSize: 13.5, fontWeight: 700, color: "var(--gray-900)", margin: 0 }}>
                {retentionPolicy.title}
              </h4>
            </div>

            <p style={{ fontSize: 12, color: "var(--gray-600)", margin: "0 0 6px" }}>
              {retentionPolicy.periodLabel}
            </p>
            <span
              style={{
                display: "inline-block",
                background: "#fff",
                border: "1px solid var(--orange-100)",
                color: "var(--orange-600)",
                fontWeight: 700,
                fontSize: 13,
                padding: "4px 12px",
                borderRadius: 7,
                marginBottom: 14,
              }}
            >
              {retentionPolicy.periodValue}
            </span>

            <p style={{ fontSize: 12, color: "var(--gray-600)", lineHeight: 1.5, margin: "0 0 14px" }}>
              {retentionPolicy.note}
            </p>

            <button
              type="button"
              onClick={() => setShowRetentionModal(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12.5,
                fontWeight: 600,
                color: "var(--blue-700)",
                background: "#fff",
                border: "1px solid var(--gray-200)",
                borderRadius: 7,
                padding: "8px 12px",
                textDecoration: "none",
                width: "100%",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              View Retention Policy
              <ExternalLink size={12} />
            </button>
          </div>
        )}
      </div>

      {showRetentionModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            zIndex: 1000,
          }}
          onClick={() => setShowRetentionModal(false)}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 640,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 20px 50px rgba(0,0,0,0.22)",
              padding: 24,
              maxHeight: "85vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: "var(--gray-900)", margin: 0 }}>
                Retention Policy
              </h3>
              <button
                type="button"
                onClick={() => setShowRetentionModal(false)}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "var(--gray-600)",
                  fontSize: 22,
                  cursor: "pointer",
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>

            <p style={{ fontSize: 14, color: "var(--gray-600)", lineHeight: 1.6, margin: "0 0 12px" }}>
              This policy explains how your consent data is retained, controlled, and withdrawn.
            </p>

            <ul style={{ paddingLeft: 18, margin: 0, color: "var(--gray-700)", lineHeight: 1.7, fontSize: 14 }}>
              <li>Your consent record is retained for the period stated in the active policy.</li>
              <li>Optional-purpose consent can be withdrawn at any time from your dashboard.</li>
              <li>Access to your data is limited to the authorized application and support team.</li>
              <li>You may request deletion or export of your data by contacting the administrator.</li>
            </ul>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 22,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "#fff",
            border: "1px solid var(--gray-200)",
            color: "var(--gray-700)",
            borderRadius: 8,
            padding: "10px 16px",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <ArrowLeft size={15} />
          Back to Privacy Notice
        </button>

        <button
          type="button"
          onClick={onContinue}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: "var(--blue-700)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 18px",
            fontSize: 13.5,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Continue to Review
          <ArrowRight size={15} />
        </button>
      </div>
    </section>
  );
}
